import { useMemo } from "react";
import {
  validateExportListFilters,
  type ExportListFilters,
  type ExportListStatusFilter,
} from "@/domain/export/filterExportJobs";
import { FilterValidationAlert } from "@/features/invoices/components/FilterValidationAlert";
import { IsoDateInput } from "@/features/invoices/components/IsoDateInput";
import { cn } from "@/utils/cn";
import { startOfLocalToday } from "@/utils/format/isoDate";

interface ExportsTableToolbarProps {
  pageSize: number;
  draft: ExportListFilters;
  isDirty: boolean;
  searchLoading?: boolean;
  onPageSizeChange: (size: number) => void;
  onDraftChange: (patch: Partial<ExportListFilters>) => void;
  onSearch: () => void;
  onClear: () => void;
  /** Pinned above scroll region on Export History. */
  pinned?: boolean;
}

export function ExportsTableToolbar({
  pageSize,
  draft,
  isDirty,
  searchLoading = false,
  onPageSizeChange,
  onDraftChange,
  onSearch,
  onClear,
  pinned = false,
}: ExportsTableToolbarProps) {
  const filterIssues = useMemo(
    () => validateExportListFilters(draft),
    [draft],
  );
  const maxDate = startOfLocalToday();
  const canSearch = filterIssues.length === 0;

  const handleSearch = () => {
    if (!canSearch || searchLoading) return;
    onSearch();
  };

  return (
    <div
      className={cn(
        "exports-table-toolbar table-toolbar flex-col",
        pinned
          ? "exports-table-toolbar--pinned px-3 py-2"
          : "border-b border-[var(--border-soft)] px-4 py-3",
      )}
    >
      <div className="flex w-full flex-wrap items-center gap-3">
        <div className="left">
          <label className="flex items-center gap-2 text-[var(--muted)]">
            Show
            <select
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
            >
              {[10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            entries
          </label>
        </div>
        <div className="left gap-2">
          <label className="flex items-center gap-1 text-[var(--muted)]">
            Status
            <select
              className="ml-1"
              value={draft.status}
              onChange={(e) =>
                onDraftChange({
                  status: e.target.value as ExportListStatusFilter,
                })
              }
            >
              <option value="all">All</option>
              <option value="running">Running</option>
              <option value="ready">Ready</option>
              <option value="failed">Failed</option>
            </select>
          </label>
          <label className="flex items-center gap-1.5 text-[var(--muted)]">
            <span className="shrink-0">From</span>
            <IsoDateInput
              className="w-[9.75rem]"
              value={draft.dateFrom}
              onChange={(dateFrom) => onDraftChange({ dateFrom })}
              aria-label="Filter exports from date"
              maxDate={maxDate}
            />
          </label>
          <label className="flex items-center gap-1.5 text-[var(--muted)]">
            <span className="shrink-0">To</span>
            <IsoDateInput
              className="w-[9.75rem]"
              value={draft.dateTo}
              onChange={(dateTo) => onDraftChange({ dateTo })}
              aria-label="Filter exports to date"
              maxDate={maxDate}
            />
          </label>
        </div>
        <div className="exports-filter-actions ml-auto flex flex-wrap items-center gap-2">
          <span
            className={cn(
              "rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-800",
              !isDirty && "invisible",
            )}
            aria-hidden={!isDirty}
          >
            Unsaved changes — Search to apply
          </span>
          <button
            type="button"
            className="btn-soft text-sm"
            onClick={onClear}
          >
            Clear
          </button>
          <button
            type="button"
            className="btn-main filter-search-btn text-sm disabled:cursor-not-allowed disabled:opacity-50"
            onClick={handleSearch}
            disabled={!canSearch || searchLoading}
            aria-busy={searchLoading || undefined}
            aria-label={searchLoading ? "Searching" : "Search"}
          >
            Search
          </button>
        </div>
      </div>

      <FilterValidationAlert
        messages={filterIssues}
        title="Fix date range to filter exports"
      />
    </div>
  );
}
